import { getActiveTenantId, isSuperAdmin, LOCALE_CACHE_KEY, TENANT_SCOPE_KEY } from './tenancy';

const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. You'll need to log in again. Unsaved changes will be lost. Continue?";
const DEFAULT_API_URL = 'https://api.fleetnexis.datadove.tech';
const FLEETNEXIS_API_URL = process.env.REACT_APP_API_URL || DEFAULT_API_URL;

let fleetnexisApiOrigin;
try {
  fleetnexisApiOrigin = new URL(FLEETNEXIS_API_URL).origin;
} catch {
  fleetnexisApiOrigin = new URL(DEFAULT_API_URL).origin;
}

let sessionExpiryPromptOpen = false;

/*
 * Session start/end. Everything the panel keeps in localStorage (token, user,
 * a super admin's tenant scope) and sessionStorage (the tenant's locale) belongs
 * to one login, so both are wiped whenever a session starts or ends. The app is
 * then reloaded on the dashboard: logout used to keep the current URL, so the
 * next person to log in landed on the previous user's page.
 */
const SESSION_KEYS = ['fleetnexis_token', 'user'];

const reloadOnDashboard = () => {
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#/`);
  window.location.reload();
};

const clearSessionStorage = () => {
  window.localStorage.clear();
  window.sessionStorage.clear();
};

export const startSession = (token, user) => {
  clearSessionStorage();
  window.localStorage.setItem('fleetnexis_token', token);
  window.localStorage.setItem('user', JSON.stringify(user));
  reloadOnDashboard();
};

export const endSession = () => {
  clearSessionStorage();
  reloadOnDashboard();
};

/*
 * Other tabs share localStorage. When one tab logs in or out, the rest would
 * keep showing the previous user's page while their requests already carry the
 * new token, so they start over too. A super admin switching tenant in another
 * tab reloads this one on the same page with that tenant's data and locale.
 * Returns the cleanup function.
 */
export const watchSessionInOtherTabs = () => {
  const onStorage = (event) => {
    if (event.storageArea !== window.localStorage) return;
    // key is null when the other tab cleared localStorage (logout / login).
    if (event.key === null || SESSION_KEYS.includes(event.key)) {
      reloadOnDashboard();
    } else if (event.key === TENANT_SCOPE_KEY) {
      window.sessionStorage.removeItem(LOCALE_CACHE_KEY);
      window.location.reload();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};

export const attachBearerToken = (config) => {
  const token = localStorage.getItem('fleetnexis_token');

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    // Super admins pick the tenant they act for; the API ignores this header
    // for everyone else.
    const tenantId = getActiveTenantId();
    // A request can set its own (e.g. 'all' for the cross-tenant overview).
    if (isSuperAdmin() && tenantId && !config.headers['X-Tenant-Id']) {
      config.headers['X-Tenant-Id'] = String(tenantId);
    }
  }

  return config;
};

export const isFleetNexisApiRequest = (requestUrl = '') => {
  if (!requestUrl) {
    return false;
  }

  try {
    const resolvedUrl = new URL(requestUrl, FLEETNEXIS_API_URL);
    return resolvedUrl.origin === fleetnexisApiOrigin;
  } catch {
    return requestUrl.startsWith(FLEETNEXIS_API_URL) || requestUrl.startsWith('/');
  }
};

export const handleSessionExpiry = (requestUrl = '') => {
  if (requestUrl.includes('/users/userLogin')) {
    return false;
  }

  if (sessionExpiryPromptOpen) {
    return false;
  }

  sessionExpiryPromptOpen = true;

  // eslint-disable-next-line no-alert
  const shouldReload = window.confirm(SESSION_EXPIRED_MESSAGE);

  if (!shouldReload) {
    setTimeout(() => {
      sessionExpiryPromptOpen = false;
    }, 0);
    return false;
  }

  endSession();
  return true;
};
